import { lazy, Suspense, useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSession } from '../session';
import { createApiClient } from '../api';
import type { RegistrationInfo } from '../api/endpoints/designSystem';
import { config } from '../../config';
import { AuthContext, type AuthContextValue } from './AuthContext';

// Lazy-loaded so the auth forms stay out of the main bundle (they only load
// once an anonymous/unregistered player actually hits an auth gate).
const AuthModal = lazy(() => import('./AuthModal').then((m) => ({ default: m.AuthModal })));

export function AuthProvider({
  registration,
  children,
}: {
  registration: RegistrationInfo;
  children: ReactNode;
}) {
  const session = useSession();

  const client = useMemo(
    () =>
      createApiClient({
        baseUrl: config.apiUrl,
        getToken: session.getToken,
        getEmbedKey: () => session.embedKey,
      }),
    [session.getToken, session.embedKey],
  );

  // Local "just attached" latch layered over the boot-time design_system
  // flag, so an attach success flips `registered` without a refetch.
  const [registeredNow, setRegisteredNow] = useState(false);
  const markRegistered = useCallback(() => setRegisteredNow(true), []);

  const [modalOpen, setModalOpen] = useState(false);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const requestAuth = useMemo(
    () =>
      (): Promise<boolean> =>
        new Promise<boolean>((resolve) => {
          resolverRef.current?.(false); // settle any in-flight caller before replacing
          resolverRef.current = resolve;
          setModalOpen(true);
        }),
    [],
  );

  const handleSuccess = useCallback(() => {
    // Snapshot + clear the resolver before resolving so a concurrent
    // requestAuth call can't stomp it.
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setModalOpen(false);
    resolve?.(true);
  }, []);

  const handleClose = useCallback(() => {
    resolverRef.current?.(false);
    resolverRef.current = null;
    setModalOpen(false);
  }, []);

  const registrationRequired = registration.mode === 'required';
  const registered = registration.registered || registeredNow;

  const value = useMemo<AuthContextValue>(
    () => ({ requestAuth, registrationRequired, registered, markRegistered }),
    [requestAuth, registrationRequired, registered, markRegistered],
  );

  return (
    <AuthContext value={value}>
      {children}
      {modalOpen && (
        <Suspense fallback={null}>
          <AuthModal
            client={client}
            onSuccess={handleSuccess}
            onClose={handleClose}
            markRegistered={markRegistered}
          />
        </Suspense>
      )}
    </AuthContext>
  );
}
